import React from 'react';
import { useThemeContext } from '../../context/ThemeProvider';
import type { ActionButtonProps } from '../../types';

const ActionButton: React.FC<ActionButtonProps> = ({ 
  onClick, 
  icon, 
  text, 
  tooltip, 
  disabled = false, 
  loading = false,
  className = "",
  variant = "primary",
  size = "normal"
}): JSX.Element => {
  const { getClass } = useThemeContext();

  const getThemeClass = (type: string): string => {
    try {
      return getClass(type) || '';
    } catch {
      const fallback: Record<string, string> = {
        background: 'bg-white',
        text: 'text-gray-900',
        button: 'bg-blue-600 hover:bg-blue-700 text-white',
        buttonSecondary: 'bg-gray-600 hover:bg-gray-700 text-white',
        buttonDanger: 'bg-red-600 hover:bg-red-700 text-white',
        buttonSuccess: 'bg-green-600 hover:bg-green-700 text-white',
        buttonWarning: 'bg-yellow-600 hover:bg-yellow-700 text-white',
        buttonInfo: 'bg-cyan-600 hover:bg-cyan-700 text-white',
        buttonDisabled: 'bg-gray-400 text-gray-200 cursor-not-allowed',
        border: 'border-gray-300',
      };
      return fallback[type] || '';
    }
  };

  const getVariantClass = (): string => {
    if (disabled || loading) {
      return getThemeClass('buttonDisabled');
    }
    
    switch (variant) {
      case 'secondary':
        return getThemeClass('buttonSecondary') || getThemeClass('button');
      case 'danger':
        return getThemeClass('buttonDanger') || getThemeClass('button');
      case 'success':
        return getThemeClass('buttonSuccess') || getThemeClass('button');
      case 'warning':
        return getThemeClass('buttonWarning') || getThemeClass('button');
      case 'info':
        return getThemeClass('buttonInfo') || getThemeClass('button');
      default:
        return getThemeClass('button');
    }
  };

  const getSizeClass = (): string => {
    switch (size) {
      case 'small':
        return 'px-2 py-1 text-xs';
      case 'large':
        return 'px-4 py-2 md:px-6 md:py-3 text-base';
      default:
        return 'px-2 py-1 md:px-3 md:py-1.5 text-sm';
    }
  };

  const buttonClass = `
    flex items-center justify-center rounded transition-all duration-200 
    hover:scale-105 active:scale-95 transform-gpu md:space-x-1
    ${getVariantClass()}
    ${getSizeClass()}
    ${disabled || loading ? 'cursor-not-allowed' : 'cursor-pointer'}
    ${className}
  `.trim();

  return (
    <button
      className={buttonClass}
      onClick={disabled || loading ? undefined : onClick}
      disabled={disabled || loading}
      title={tooltip}
    >
      {icon && <span className="text-base">{icon}</span>}
      {text && (
        <span className="hidden lg:inline ml-1">
          {loading ? (text + '...') : text}
        </span>
      )}
    </button>
  );
};

export default ActionButton; 